import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import OrderConfirmationButton from './OrderConfirmationButton';

export default function SubmittedStage({ aiOrderNumber, poNumber, splits, alreadySubmitted, correlationId, onReset }) {
  // Order confirmation PDF (Tonya, 2026-10-22): one per A+ order, so one per successful split,
  // each offered once its order is in A+.
  // Multi-warehouse split (backend, 2026-09-04): splits is present whenever the order's
  // lines shipped from more than one warehouse, and replaces the single order-number
  // view with a per-warehouse breakdown. Any split that succeeded is already a real A+
  // order, so this screen is the only place that result is ever shown — there is no
  // "retry" here, since retrying would resubmit the splits that already went through.
  const hasSplits = Array.isArray(splits) && splits.length > 0;
  const allSucceeded = hasSplits && splits.every(s => s.success);
  const anySucceeded = hasSplits && splits.some(s => s.success);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
      <div className="text-center max-w-lg w-full">
        <div
          className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${
            !hasSplits || allSucceeded ? 'bg-emerald-500 animate-bounce' : anySucceeded ? 'bg-amber-500' : 'bg-red-500'
          }`}
        >
          {!hasSplits || allSucceeded ? (
            <CheckCircle className="w-12 h-12 text-white" />
          ) : anySucceeded ? (
            <AlertTriangle className="w-12 h-12 text-white" />
          ) : (
            <XCircle className="w-12 h-12 text-white" />
          )}
        </div>

        {!hasSplits ? (
          <>
            <h2 className="text-3xl font-bold text-white mb-2">Order Submitted!</h2>
            <p className="text-emerald-400 font-mono text-lg mb-2">
              Your Order {aiOrderNumber} has been sent to A+
            </p>
            {alreadySubmitted && (
              <p className="max-w-md mx-auto mb-2 px-4 py-2 rounded-lg bg-sky-500/10 text-sky-300 text-sm flex items-start gap-2 text-left">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>This order had already been submitted, so it wasn't sent again. There is no duplicate in A+.</span>
              </p>
            )}
          </>
        ) : (
          <>
            <h2 className="text-3xl font-bold text-white mb-2">
              {allSucceeded
                ? 'Order Submitted!'
                : anySucceeded
                  ? 'Order Partially Submitted'
                  : 'Order Submission Failed'}
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              This PO shipped from {splits.length} warehouses and was submitted to A+ as {splits.length} separate orders.
            </p>
            <div className="text-left bg-slate-800/60 rounded-lg divide-y divide-slate-700 mb-2">
              {splits.map((s, i) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm text-white font-mono">
                      {s.success ? (s.aiOrderNumber || '(order number unavailable)') : 'Not submitted'}
                    </div>
                    <div className="text-xs text-slate-500">Warehouse {s.warehouseId}</div>
                  </div>
                  {s.success ? (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {correlationId && s.aiOrderNumber && (
                        <OrderConfirmationButton correlationId={correlationId} orderNumber={s.aiOrderNumber} compact />
                      )}
                      <span className="text-xs px-2 py-1 rounded bg-emerald-500/15 text-emerald-400 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Sent to A+
                      </span>
                    </div>
                  ) : (
                    <span
                      className="text-xs px-2 py-1 rounded bg-red-500/15 text-red-400 flex items-center gap-1 flex-shrink-0"
                      title={s.error || ''}
                    >
                      <XCircle className="w-3 h-3" /> Failed
                    </span>
                  )}
                </div>
              ))}
            </div>
            {!allSucceeded && (
              <p className="text-xs text-amber-400 mb-6">
                {anySucceeded
                  ? 'The warehouse(s) marked "Failed" above did not reach A+ — do not resubmit this PO, since the successful order(s) above would be duplicated. Contact support with this PO number to resolve the failed warehouse(s).'
                  : 'None of this order reached A+. Contact support with this PO number.'}
              </p>
            )}
          </>
        )}

        {poNumber && (
          <p className="text-slate-400 text-sm mb-8">PO: {poNumber}</p>
        )}
        {!hasSplits && correlationId && (
          <div className="mb-6">
            <OrderConfirmationButton correlationId={correlationId} orderNumber={aiOrderNumber} />
          </div>
        )}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={onReset}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Process Another Order
          </button>
        </div>
      </div>
    </div>
  );
}
